
import { mapState, mapMutations } from 'vuex';
import ClinicSummary from '@/components/clinicSummary';
import Staff from '@/components/staff.vue';

export default {
  name: 'Map',

  components: {
    ClinicSummary,
    Staff,
  },

  props: {
    showClinics: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      map: null,
      markers: [],
      infoWindows: [],
      working: false,
    };
  },

  computed: {
    ...mapState(['gmapLoaded', 'clinics', 'closestClinicId']),

    closestClinic() {
      return this.clinics.find((c) => c.id === this.closestClinicId);
    },

    zoom() {
      switch (this.$i18n.locale) {
        case 'fr':
          return 5;
        case 'es':
          return 6;
        case 'pt':
        default:
          return 10;
      }
    },

    center() {
      if (process.env.REGION === 'AO') {
        return { lat: -8.838333, lon: 13.234444 };
      }
      switch (this.$i18n.locale) {
        case 'ao':
          return { lat: -8.838333, lon: 13.234444 };
        case 'fr':
          return { lat: 47.257288, lon: 2.42027 };
        case 'es':
          return { lat: 40.422776, lon: -3.704944 };
        case 'pt':
        default:
          return { lat: 38.722252, lon: -9.139337 };
      }
    },

    myClinicId: {
      get() {
        return this.closestClinicId || null;
      },
      set(value) {
        this.setClosestClinic(value);
        this.map.panTo(
          new window.google.maps.LatLng(
            this.closestClinic.fields.address.lat,
            this.closestClinic.fields.address.lng
          )
        );
        this.smoothZoom(this.zoom);
      },
    },
  },

  mounted() {
    if (this.gmapLoaded) {
      this.drawMap();
    }
  },

  methods: {
    ...mapMutations(['markGmapAsLoaded', 'setClosestClinic']),

    init() {
      this.markGmapAsLoaded();
      this.drawMap();
    },

    toClinic() {
      this.$router.push(
        this.localePath({
          name: 'clinic-slug',
          params: { slug: this.closestClinic.slug },
        })
      );
    },

    drawMap() {
      this.map = new window.google.maps.Map(this.$refs.map, {
        center: new window.google.maps.LatLng(this.center.lat, this.center.lon),
        zoom: this.zoom,
      });
      if (this.showClinics && this.clinics.length) {
        this.addMarkers();
      }
    },

    getClinicPrefixUrl() {
      switch (this.$i18n.locale) {
        case 'pt':
          return 'clinicas-lev';
        case 'es':
          return 'centros-lev';
        case 'fr':
          return 'centre-lev-diet';
        default:
          return 'lev-clinics';
      }
    },

    addMarkers() {
      this.clinics.forEach((clinic, i) => {
        this.markers[i] = new window.google.maps.Marker({
          position: {
            lat: clinic.fields.address.lat,
            lng: clinic.fields.address.lng,
          },
          map: this.map,
          icon: {
            url: '/Lev-map.png',
            scaledSize: { width: 80, height: 90 },
          },
        });
        const windowContent = `
        <a href="/${this.getClinicPrefixUrl()}/${clinic.slug}">
          <div class="info-window">
            <h1>${clinic.title.rendered}</h1>
            <div id="info-window__content">
              <p>${clinic.fields.address?.address}</p>
              <p>${clinic.fields.email}</p>
              <p>${
                clinic.fields.phones.length
                  ? this.$t('global.phone') +
                    '.<span> ' +
                    clinic.fields.phones[0].phone +
                    '</span>'
                  : null
              }</p>
              <a class="arrow" href="/${this.getClinicPrefixUrl()}/${
          clinic.slug
        }"></a>
            </div>
          </div>
        </a>
          `;
        this.infoWindows[i] = new window.google.maps.InfoWindow({
          content: windowContent,
        });
        this.markers[i].addListener('click', () => {
          this.setClosestClinic(clinic.id);
          this.infoWindows[i].open({
            anchor: this.markers[i],
            map: this.map,
            shouldfocus: false,
          });
        });
      });
    },

    smoothZoom(zoom) {
      const currentZoom = arguments[1] || this.map.getZoom();
      if (currentZoom !== zoom) {
        window.google.maps.event.addListenerOnce(
          this.map,
          'zoom_changed',
          () => {
            this.smoothZoom(zoom, currentZoom + (zoom > currentZoom ? 1 : -1));
          }
        );
        setTimeout(() => this.map.setZoom(currentZoom), 80);
      }
    },

    findClosest() {
      if (navigator.geolocation) {
        this.working = true;
        this.smoothZoom(4);
        navigator.geolocation.getCurrentPosition(
          this.positionSuccess,
          this.positionError
        );
      }
    },

    positionSuccess(pos) {
      const closestClinic = this.$geo.closestClinic(pos.coords, this.clinics);
      this.working = false;
      this.map.panTo(
        new window.google.maps.LatLng(
          closestClinic.fields.address.lat,
          closestClinic.fields.address.lng
        )
      );
      this.smoothZoom(11);
      this.setClosestClinic(closestClinic.id);
    },

    positionError() {
      this.working = false;
    },
  },

  head() {
    return this.gmapLoaded
      ? {}
      : {
          script: [
            {
              hid: 'maps-googleapis',
              src: `https://maps.googleapis.com/maps/api/js?libraries=places&key=${process.env.GOOGLE_MAP}`,
              defer: true,
              callback: this.init,
            },
          ],
        };
  },
};
